import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  EventEmitter,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  Output,
  Signal,
  signal,
  untracked,
  ViewEncapsulation,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { BodyComponent } from '@shared/components/body/body.component';
import { BoxComponent } from '@shared/components/box/box.component';
import { FooterComponent } from '@shared/components/footer/footer.component';
import { AutofillComponent } from '@shared/components/form/autofill/autofill.component';
import { SelectComponent } from '@shared/components/form/select/select.component';
import { TextboxComponent } from '@shared/components/form/textbox/textbox.component';
import { HeaderComponent } from '@shared/components/header/header.component';
import { SubtitleBlockComponent } from '@shared/components/subtitle-block/subtitle-block.component';
import { Months } from '@shared/shared.constants';
import { SealFindByKeyPipe } from '@shipsfocus/seal';
import { EditInventorySupplyComponent } from '@vessel-reports/rob/components/edit-inventory-supply/edit-inventory-supply.component';
import { ReportStatuses } from '@vessel-reports/rob/rob.constants';
import { RobActions } from '@vessel-reports/rob/state/rob.action';
import { RobState } from '@vessel-reports/rob/state/rob.state';
import { Detail, RobReportDetail, RobReportType } from '@vessel-reports/rob/state/rob.types';
import { VesselReportsAction } from '@vessel-reports/state/vessel-reports.action';
import { Observable, Subject, Subscription, take, takeUntil } from 'rxjs';

import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList } from '@angular/cdk/drag-drop';
import { MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Params } from '@angular/router';
import { PopupService } from '@app/core/services/popup.service';
import { PermissionPipe } from '@app/features/admin/settings/permission/pipes/permission.pipe';
import { VesselReportApi } from '@chartering/vessel-reports/vessel-reports.constants';
import { TranslocoPipe, TranslocoService } from '@ngneat/transloco';
import { GridComponent } from '@shared/components/grid/grid.component';
import { StatusBadgeComponent } from '@shared/components/status-badge/status-badge.component';
import { InventorySuppliesComponent } from '@vessel-reports/rob/components/inventory-supplies/inventory-supplies.component';
import { RobService } from '@vessel-reports/rob/state/rob.service';
import { MathRoundPipe } from 'app/core/pipe/math-round.pipe';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';

@Component({
  selector: 'rob-report-details',
  imports: [
    TranslocoPipe,
    HeaderComponent,
    BoxComponent,
    BodyComponent,
    SubtitleBlockComponent,
    SealFindByKeyPipe,
    NgClass,
    SelectComponent,
    TextboxComponent,
    MatIcon,
    FooterComponent,
    AutofillComponent,
    EditInventorySupplyComponent,
    ReactiveFormsModule,
    MathRoundPipe,
    InventorySuppliesComponent,
    CdkDragHandle,
    CdkDropList,
    CdkDrag,
    MatIconButton,
    StatusBadgeComponent,
    GridComponent,
    PermissionPipe
  ],
  templateUrl: './rob-report-details.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    .mat-mdc-dialog-container .mdc-dialog__surface {
      padding: 0 !important;
      min-width: 300px !important;
    }

    .grid-edit {
      grid-template-columns: 30px 710fr 150fr 24px !important;

      &-header {
        @apply whitespace-nowrap text-base font-bold;
      }
    }

    .grid-edit-submit {
      grid-template-columns: 710fr 150fr !important;

      &-header {
        @apply whitespace-nowrap text-base font-bold;
      }
    }

    .grid-inventory {
      grid-template-columns: 400fr 334fr 150fr 48px !important;

      &-header {
        @apply whitespace-nowrap text-base font-semibold;
      }
    }
  `
})
export class RobReportDetailsComponent implements OnInit, OnDestroy {
  protected readonly translocoService: TranslocoService = inject(TranslocoService);
  protected readonly popupService: PopupService = inject(PopupService);

  mode: WritableSignal<RobReportType> = signal('details');
  protected readonly months: typeof Months = Months;
  protected readonly state: RobState = inject(RobState);
  private readonly _action: RobActions = inject(RobActions);
  private readonly _vesselReportsActions: VesselReportsAction = inject(VesselReportsAction);
  private readonly _activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly _changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly _formBuilder: FormBuilder = inject(FormBuilder);
  private readonly _injector: Injector = inject(Injector);
  readonly _matDialog: MatDialog = inject(MatDialog);
  protected readonly destroyRef: DestroyRef = inject(DestroyRef);
  protected readonly isEmpty: typeof isEmpty = isEmpty;
  readonly detailReport: Signal<RobReportDetail> = computed(() => this.state.detailReport());
  readonly selectedDetail: Signal<Detail> = computed(() => this.state.selectedDetail());
  readonly statuses: typeof ReportStatuses = ReportStatuses;
  readonly inventories: Signal<any[]> = computed(() => this.state.inventories());
  protected readonly VesselReportApi: typeof VesselReportApi = VesselReportApi;
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  form: FormGroup;
  supplyForm: FormGroup;
  queryParams: WritableSignal<Params> = signal(void 0);
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() close: EventEmitter<any> = new EventEmitter<any>();
  private _controlSubscriptions: Map<string, Subscription> = new Map<string, Subscription>();

  constructor(private _service: RobService) {}

  ngOnInit() {
    this.form = this._formBuilder.group({
      year: ['', [Validators.required]],
      month: ['', [Validators.required]],
      date_report: ['', [Validators.required]],
      vessel_report_id: [''],
      vessel_id: [],
      remarks: [''],
      flag_copy: [false],
      report_type_id: [9],
      status_id: [1],
      supplies: this._formBuilder.array([]),
      details: this._formBuilder.array([])
    });
    this.supplyForm = this._formBuilder.group({
      supplies: this._formBuilder.array([])
    });

    this._subscribeToSource$(this._activatedRoute.queryParams, (queryParams) => {
      this.queryParams.set({ ...queryParams });
    });
    effect(
      () => {
        if (this.detailReport()) {
          untracked(() => {
            this.updateDetailReport();
          });
        }
      },
      { injector: this._injector }
    );
  }

  private _subscribeToSource$<T>(source$: Observable<T>, callback: (value: T) => void): void {
    source$.pipe(takeUntil(this._unsubscribeAll)).subscribe((value: T) => {
      callback(value);

      this._changeDetectorRef.markForCheck();
    });
  }

  ngOnDestroy() {
    this._clearControlSubscriptions();
    this._action.actions.cleanState();
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  createDetailFormGroup(data: Detail): FormGroup {
    return this._formBuilder.group({
      id: [data.id],
      report_item_id: [data.report_item_id ? data.report_item_id.toString() : '0'],
      value: [data.value ? Number(data.value) : ''],
      inventory_item_id: [data.inventory_item_id],
      report_item_name: [data.report_item_name],
      report_item_unit: [data.report_item_unit],
      description: [data.description],
      remarks: [data.remarks || ''],
      inventory_item_name: [data.inventory_item_name],
      inventory_item_unit: [data.inventory_item_unit]
    });
  }

  updateDetailReport() {
    if (this.detailReport()) {
      try {
        const detailsArray = this.form.get('details') as FormArray;
        detailsArray.clear();
        this.form.patchValue(this.detailReport());
        this.detailReport().details?.forEach((item: Detail) => {
          const control = this.createDetailFormGroup(item);
          detailsArray.push(control);
        });
        this.supplyForm.get('supplies').patchValue(this.detailReport().supplies);
        this.mode.set(this.detailReport().status_id === 1 ? 'details' : 'review');
      } catch (e) {
        console.log('e', e);
      }
    }
  }

  get details(): FormArray {
    return this.form.get('details') as FormArray;
  }

  get title() {
    if (!this.detailReport()) {
      return '';
    }
    return this.detailReport()?.vessel_name + ' - ' + 'Monthly ROB Report';
  }

  getOrdinal(n: number): string {
    if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`;
    switch (n % 10) {
      case 1:
        return `${n}st`;
      case 2:
        return `${n}nd`;
      case 3:
        return `${n}rd`;
      default:
        return `${n}th`;
    }
  }

  get subtitle() {
    if (this.detailReport()?.status_id !== 1) {
      const lastDay = new Date(this.detailReport().year, this.detailReport().month, 0);
      const date = dayjs(lastDay.getTime());
      return 'Inventory as of ' + this.getOrdinal(date.date()) + ' ' + date.format('MMMM YYYY') + ' 24:00';
    }
    return '';
  }

  trackById(index: number, item: any): number | string {
    return `${index}_${item.id}`;
  }

  onDeleteInventory(index: number, id: string) {
    this.popupService.openConfirmationDialog({
      title: this.translocoService.translate('ship_interface.delete_inventory'),
      message: this.translocoService.translate('ship_interface.are_you_sure_you_want_to_delete_this_inventory_this_action_cannot_be_undone'),
      actionType: 'delete',
      onAccept: () => {
        this._action.actions.deleteDetail({ id: id });
        this._action.actions.deleteDetailSuccess$.pipe(take(1), takeUntil(this._unsubscribeAll)).subscribe(() => {
          this.details.removeAt(index);
          this.getDetailReportWithCallback();
        });
      },
      onCancel: () => {}
    });
  }

  onEditInventory(detail: Detail) {
    this.state.set({
      selectedDetail: detail
    });
    this.mode.set('input-inventory');
  }

  onDiscard() {
    this._matDialog.closeAll();
  }

  onSave() {
    let formData: FormData = new FormData();
    if (this.detailReport().status_id === 3) {
      this._action.actions.updateReport({
        report_id: this.detailReport().id,
        body: {
          status_id: 2,
          date_report: this.detailReport().date_report
        }
      });
      this._action.actions.updateReportSuccess$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.updateDetailReport();
        this.getVesselsReport({
          ...this.queryParams(),
          'filter[report_type_id]': 9
        });
        this.mode.set(this.detailReport().status_id === 1 ? 'details' : 'review');
      });
      return;
    }
    if (this.detailReport().status_id === 1) {
      this.form.get('status_id').setValue(2);
    } else if (this.detailReport().status_id === 2) {
      this.form.get('status_id').setValue(3);
    }
    formData = this.convertFormToFormData(this.form);
    this._action.actions.updateDetailReport({ body: formData });
    this._action.actions.updateDetailReportSuccess$.pipe(take(1), takeUntil(this._unsubscribeAll)).subscribe(() => {
      this.getDetailReportWithCallback(() => this.mode.set(this.detailReport().status_id === 1 ? 'details' : 'review'));
      this.getVesselsReport({
        ...this.queryParams(),
        ...{
          'filter[report_type_id]': 9
        }
      });
    });
  }

  convertFormToFormData(form: FormGroup): FormData {
    const formData = new FormData();
    formData.append('vessel_report_id', this.detailReport().id);
    formData.append('year', this.form.get('year').value.toString());
    formData.append('month', this.form.get('month').value.toString());
    formData.append('remarks', this.form.get('remarks')?.value || '');
    formData.append('status_id', this.form.get('status_id').value.toString());
    const detailsArray = form.get('details') as FormArray;

    detailsArray.controls.forEach((group: AbstractControl, index: number) => {
      const groupValue = group.value;
      for (const key in groupValue) {
        if (Object.prototype.hasOwnProperty.call(groupValue, key)) {
          groupValue[key] && formData.append(`details[${index}][${key}]`, groupValue[key]);
        }
      }
    });

    return formData;
  }

  onSubmit() {
    this.onSave();
  }

  onDelete() {
    this.popupService.openConfirmationDialog({
      title: this.translocoService.translate('ship_interface.delete_report'),
      message: this.translocoService.translate('ship_interface.are_you_sure_you_want_to_delete_this_report_this_action_cannot_be_undone'),
      actionType: 'delete',
      onAccept: () => {
        this._action.actions.deleteReport({ report_id: this.detailReport().id });
        this._action.actions.deleteReportSuccess$.pipe(take(1), takeUntil(this._unsubscribeAll)).subscribe(() => {
          this.getVesselsReport({
            ...this.queryParams(),
            'filter[report_type_id]': 9
          });
          this._matDialog.closeAll();
        });
      },
      onCancel: () => {}
    });
  }

  onInventories(detail: any) {
    this.state.set({
      selectedDetail: detail.value
    });
    this.mode.set('select-inventories');
  }

  onBack() {
    this.mode.set(this.detailReport().status_id === 1 ? 'details' : 'review');
  }

  private _clearControlSubscriptions(): void {
    this._controlSubscriptions.forEach((subscription) => subscription?.unsubscribe());
    this._controlSubscriptions.clear();
  }

  onSaveInventory(id: number) {
    this.mode.set(this.detailReport().status_id === 1 ? 'details' : 'review');
    this.findDetailById(this.selectedDetail()?.id)?.get('inventory_item_id')?.setValue(id);
  }

  findDetailById(targetId: string): FormGroup | undefined {
    const details = this.form.get('details') as FormArray;

    return details.controls.find((control) => control.get('id')?.value === targetId) as FormGroup | undefined;
  }

  drop(event: CdkDragDrop<string[]>): void {
    const form = new FormData();
    form.append('vessel_report_id', String(this.detailReport().id));
    form.set(`details[${event.previousIndex}][id]`, this.detailReport().details[event.previousIndex]?.id);
    form.set(`details[${event.previousIndex}][sequence]`, String(this.detailReport().details[event.currentIndex]?.sequence));
    form.set(`details[${event.currentIndex}][id]`, this.detailReport().details[event.currentIndex]?.id);
    form.set(`details[${event.currentIndex}][sequence]`, String(this.detailReport().details[event.previousIndex]?.sequence));

    this._action.actions.updateRobSequence({ body: form });
    this._action.actions.updateRobSequenceSuccess$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.getDetailReportWithCallback(() => this.updateDetailReport());
    });
  }

  getDetailReportWithCallback(cb?: () => void): void {
    this._action.actions.getDetailReport({ report_id: this.detailReport().id });
    if (cb) {
      this._action.actions.getDetailReportSuccess$.pipe(take(1), takeUntil(this._unsubscribeAll)).subscribe(() => {
        cb();
      });
    }
  }

  getVesselsReport(queryParams: Params): void {
    this._vesselReportsActions.actions.getVesselReports({ queryParams });
  }
}
